import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import apiService from '../services/api';
import liffService from '../services/liff';
import SkillRadarChart from './SkillRadarChart';
import RankBadge from './RankBadge';
import { resolveTierColor } from '../constants/rankColors';
import {
  RadarObject, SkillObject, StoreUser, UnacquiredSkillObject, TierObject,
} from '../types';

interface GamificationSectionProps {
  /** 横断ランク用（User.id） */
  userId: string | null;
  /** 「この店舗での評価」用に選択する店舗（StoreUser.id を keyed に per-store API を叩く） */
  storeUsers: StoreUser[];
  /** 獲得スキルセクションを非表示にする（編集モード時など） */
  hideSkills?: boolean;
}

// 獲得スキルのトロフィーカード（横並びレイアウト）
const SkillTrophyCard: React.FC<{ skill: SkillObject }> = ({ skill }) => {
  const color = resolveTierColor(skill.tier?.code, skill.tier?.color);
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${color}33`,
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        px: 1.5,
        py: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: `${color}1A`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <EmojiEventsRoundedIcon sx={{ color, fontSize: 22 }} />
      </Box>
      <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.3, color: '#3c3c3c' }}>
        {skill.name}
      </Typography>
    </Box>
  );
};

// 未習得スキルのカード（グレーアウト + マニュアルURLがあればリンク表示）
const UnacquiredSkillCard: React.FC<{ skill: UnacquiredSkillObject }> = ({ skill }) => {
  const color = resolveTierColor(skill.tier?.code, skill.tier?.color);
  const hasUrl = !!skill.manual_url;
  const handleOpenManual = () => {
    if (!skill.manual_url) return;
    liffService.openWindow(skill.manual_url, true);
  };
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        bgcolor: '#fafafa',
        px: 1.5,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <EmojiEventsRoundedIcon sx={{ color: '#bdbdbd', fontSize: 22 }} />
          <LockOutlinedIcon
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              fontSize: 14,
              color: '#9e9e9e',
              bgcolor: '#fafafa',
              borderRadius: '50%',
            }}
          />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.3, color: '#757575' }}>
            {skill.name}
          </Typography>
          <Typography sx={{ fontSize: '10px', color: color, lineHeight: 1.2 }}>
            {skill.tier?.name ?? ''}
          </Typography>
        </Box>
      </Box>
      {hasUrl && (
        <Box
          onClick={handleOpenManual}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            color: '#1976d2',
            pl: 0.5,
            mt: 0.25,
            '&:active': { opacity: 0.6 },
          }}
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 600, textDecoration: 'underline' }}>
            マニュアルを見る
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * マイページ ゲーミフィケーションセクション（設計書 §6.3 / p.17-18）。
 * - 店舗選択ドロップダウン（すべての店舗での評価 / 各店舗名）
 * - Lv を大きく中央表示 → レーダー(自前SVG) → 獲得スキル 2列グリッド
 *   global → GET /api/user/{userId}/global-skill-parameters / global-skills（バックエンド実装待ち）
 *   store  → GET /api/store-users/{storeUserId}/skill-parameters / skills
 */
const GamificationSection: React.FC<GamificationSectionProps> = ({ userId, storeUsers, hideSkills = false }) => {
  // 'all' = すべての店舗 / storeUser.id = 特定店舗
  const [selected, setSelected] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [pendingGlobal, setPendingGlobal] = useState<boolean>(false); // global APIが未実装の場合

  // 既定の店舗（先頭）
  const primaryStoreUser = storeUsers[0] ?? null;

  const [radar, setRadar] = useState<RadarObject | null>(null);
  const [skills, setSkills] = useState<SkillObject[]>([]);
  const [level, setLevel] = useState<number | null>(null);
  // 評価範囲に対応するランクバッジ（all=横断ランク / 店舗=店舗内ランク）
  const [rankTier, setRankTier] = useState<TierObject | null>(null);

  // 未習得スキル（特定店舗選択時のみ取得。「自分の職種」/「すべての職種」タブで切り替え）
  const [unacquired, setUnacquired] = useState<UnacquiredSkillObject[]>([]);
  const [myPositionIds, setMyPositionIds] = useState<string[]>([]);
  const [unacquiredTab, setUnacquiredTab] = useState<'mine' | 'all'>('mine');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setPendingGlobal(false);
    setRankTier(null);
    try {
      if (selected === 'all') {
        if (!userId) {
          setUnavailable(true);
          return;
        }
        // 全店舗集計エンドポイント（レーダー/スキルが未集計の場合は「集計データ準備中」）
        const [paramsRes, skillsRes, globalRankRes] = await Promise.all([
          apiService.getGlobalSkillParameters(userId),
          apiService.getGlobalSkills(userId),
          apiService.getGlobalRank(userId),
        ]);
        setRankTier(globalRankRes.success && globalRankRes.data ? globalRankRes.data.tier ?? null : null);
        if (paramsRes.success && paramsRes.data) {
          setRadar(paramsRes.data);
          setLevel(paramsRes.data.level ?? null);
          setSkills(skillsRes.success && skillsRes.data ? skillsRes.data.skills ?? [] : []);
        } else {
          setPendingGlobal(true);
        }
        // 「すべての店舗」モードでは未習得スキルは表示しない（店舗単位の概念のため）
        setUnacquired([]);
        setMyPositionIds([]);
      } else {
        // 特定店舗
        const storeUser = storeUsers.find((su) => su.id === selected) ?? primaryStoreUser;
        if (!storeUser) {
          setUnavailable(true);
          return;
        }
        const [paramsRes, skillsRes, unacquiredRes, storeRankRes] = await Promise.all([
          apiService.getWorkerSkillParameters(storeUser.id),
          apiService.getWorkerSkills(storeUser.id),
          apiService.getUnacquiredSkills(storeUser.id),
          apiService.getStoreUserRank(storeUser.id),
        ]);
        setRankTier(storeRankRes.success && storeRankRes.data ? storeRankRes.data.tier ?? null : null);
        if (paramsRes.success && paramsRes.data) {
          setRadar(paramsRes.data);
          setLevel(paramsRes.data.level ?? null);
          setSkills(skillsRes.success && skillsRes.data ? skillsRes.data.skills ?? [] : []);
        } else {
          setUnavailable(true);
        }
        if (unacquiredRes.success && unacquiredRes.data) {
          setUnacquired(unacquiredRes.data.unacquired ?? []);
          setMyPositionIds(unacquiredRes.data.my_position_ids ?? []);
        } else {
          setUnacquired([]);
          setMyPositionIds([]);
        }
      }
    } catch {
      if (selected === 'all') {
        setPendingGlobal(true);
      } else {
        setUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  }, [selected, userId, storeUsers, primaryStoreUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectChange = (e: SelectChangeEvent) => {
    setSelected(e.target.value);
  };

  // 未習得スキルをタブで絞り込み、職種ごとに tier.sort_order 昇順でグルーピング表示する。
  // - mine: my_position_ids に含まれる職種 + 汎用枠（position_id=null）
  // - all : 全件（職種別グルーピングは同じ）
  const groupedUnacquired = useMemo(() => {
    const list = unacquired.filter((sk) => {
      if (unacquiredTab === 'all') return true;
      if (sk.position_id == null) return true; // 汎用枠は常に表示
      return myPositionIds.includes(sk.position_id);
    });
    const map = new Map<string, { positionId: string | null; positionName: string; items: UnacquiredSkillObject[] }>();
    for (const sk of list) {
      const key = sk.position_id ?? '__general__';
      if (!map.has(key)) {
        map.set(key, {
          positionId: sk.position_id ?? null,
          positionName: sk.position_name ?? '汎用',
          items: [],
        });
      }
      map.get(key)!.items.push(sk);
    }
    // 各グループ内を tier.sort_order → slot_index でソート
    for (const group of Array.from(map.values())) {
      group.items.sort((a, b) => {
        const ts = (a.tier?.sort_order ?? 0) - (b.tier?.sort_order ?? 0);
        if (ts !== 0) return ts;
        return (a.slot_index ?? 0) - (b.slot_index ?? 0);
      });
    }
    // グループ自体は API の返却順（汎用枠は末尾）を尊重
    return Array.from(map.values());
  }, [unacquired, unacquiredTab, myPositionIds]);

  const showUnacquiredSection = selected !== 'all' && !loading && !unavailable && !pendingGlobal;

  return (
    <Box sx={{ px: 4, mb: 2 }}>
      {/* 評価範囲ドロップダウン */}
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <Select
          value={selected}
          onChange={handleSelectChange}
          displayEmpty
          sx={{
            borderRadius: 2,
            bgcolor: '#fff',
            fontSize: '13px',
            fontWeight: 700,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '13px' }}>
            すべての店舗での評価
          </MenuItem>
          {storeUsers.map((su) => (
            <MenuItem key={su.id} value={su.id} sx={{ fontSize: '13px' }}>
              {su.store_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : pendingGlobal ? (
          <Typography sx={{ color: '#9e9e9e', textAlign: 'center', py: 2, fontSize: '13px' }}>
            集計データ準備中
          </Typography>
        ) : unavailable ? (
          <Typography sx={{ color: '#9e9e9e', textAlign: 'center', py: 2, fontSize: '13px' }}>
            評価データはまだありません
          </Typography>
        ) : (
          <>
            {/* 評価範囲に対応するランクバッジ（all=横断ランク / 店舗=店舗内ランク） */}
            {rankTier && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                <RankBadge tier={rankTier} size={24} labelFontSize="14px" />
              </Box>
            )}

            {/* Lv 大きく中央表示 */}
            {level != null && (
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '28px',
                  color: '#1a1a1a',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                Lv. {level}
              </Typography>
            )}

            {/* レーダー */}
            {radar && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                <SkillRadarChart data={radar} size={360} />
              </Box>
            )}

            {/* 獲得スキル 2列グリッド（編集モード中は非表示） */}
            {!hideSkills && (
              <>
                <Typography sx={{ fontSize: '19px', fontWeight: 700, mt: 3, mb: 1, textAlign: 'center' }}>獲得スキル</Typography>
                {skills.length === 0 ? (
                  <Typography sx={{ color: '#9e9e9e', fontSize: '12px', py: 1 }}>
                    まだ獲得したスキルはありません
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1,
                    }}
                  >
                    {skills.map((sk) => (
                      <SkillTrophyCard key={sk.skill_id} skill={sk} />
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* 未習得スキル（特定店舗選択時のみ表示。「自分の職種」「すべての職種」タブで切替） */}
      {!hideSkills && showUnacquiredSection && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: '19px', fontWeight: 700, mb: 1, textAlign: 'center' }}>
            未習得スキル
          </Typography>

          <Tabs
            value={unacquiredTab}
            onChange={(_, v) => setUnacquiredTab(v as 'mine' | 'all')}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              mb: 1.5,
              '& .MuiTab-root': {
                fontSize: '12px',
                fontWeight: 700,
                minHeight: 36,
                textTransform: 'none',
                color: '#9e9e9e',
              },
              '& .Mui-selected': { color: '#1976d2' },
              '& .MuiTabs-indicator': { backgroundColor: '#1976d2' },
            }}
          >
            <Tab value="mine" label="自分の職種" />
            <Tab value="all" label="すべての職種" />
          </Tabs>

          {groupedUnacquired.length === 0 ? (
            <Typography sx={{ color: '#9e9e9e', fontSize: '12px', py: 1, textAlign: 'center' }}>
              {unacquiredTab === 'mine'
                ? '自分の職種で未習得のスキルはありません'
                : '未習得のスキルはありません'}
            </Typography>
          ) : (
            groupedUnacquired.map((group) => (
              <Box key={group.positionId ?? '__general__'} sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#616161',
                    mb: 0.75,
                    pl: 0.5,
                  }}
                >
                  {group.positionName}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                  }}
                >
                  {group.items.map((sk) => (
                    <UnacquiredSkillCard key={sk.skill_definition_id} skill={sk} />
                  ))}
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default GamificationSection;
